import React from 'react';
import { UsersType } from '@/@types/user.types';
import { Table, Box, Button } from '@legion-ui/core';
import { FaUserLock, FaUsersSlash, FaSortUp, FaSortDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Indicator from '../Indicator';
import { frontConfig } from '@/config/front-config';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

interface UsersTableProps {
  users: (UsersType | undefined)[];
  isLoading: boolean;
  setUser: (user: UsersType) => void;
  startNumber?: number;
  sortOrder?: 'asc' | 'desc';
  onToggleSort?: () => void;
}

const UsersTable = ({
  users = [],
  isLoading = false,
  setUser,
  startNumber = 0,
  sortOrder = 'desc',
  onToggleSort,
}: UsersTableProps) => {
  const navigate = useNavigate();
  const { superAdminUsers } = frontConfig();

  const tableHeader = [
    'No',
    'Username',
    'Email',
    'Created at',
    'Status',
    'Action',
  ];

  const handleControlPolicy = (user: UsersType) => {
    setUser(user);
    navigate(`/users/control-policy/${user.username}`, { state: { user } });
  };

  const formatDate = (dateString?: Date) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const formatDate2 = (dateString?: string) => {
    if (!dateString) return null;

    const rawDate = dateString.endsWith('Z')
      ? dateString.slice(0, -1)
      : dateString;

    const date = new Date(rawDate);

    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const maskText = (text: string, visibleChars: number = 6) => {
    if (!text) return text;

    // Untuk username/email yang sangat pendek, tetap mask minimal 2 karakter pertama
    if (text.length <= 3) {
      return '*'.repeat(text.length - 1) + text.slice(-1);
    }

    // Untuk text pendek (4-6 karakter), tampilkan maksimal 50% karakter terakhir
    if (text.length <= visibleChars) {
      const charsToShow = Math.max(1, Math.floor(text.length / 2));
      const maskedLength = text.length - charsToShow;
      const masked = '*'.repeat(maskedLength);
      return masked + text.slice(-charsToShow);
    }

    // Untuk text panjang, gunakan visibleChars
    const maskedLength = text.length - visibleChars;
    const masked = '*'.repeat(Math.min(maskedLength, 10)); // Maximum 10 asterisks
    return masked + text.slice(-visibleChars);
  };

  const renderUsers = () => {
    const hasValidUser = users.some((u) => u && Object.keys(u).length > 0);

    if (!hasValidUser) {
      return (
        <tr>
          <td colSpan={6} className="text-center">
            <div className="min-h-[33.25rem] flex flex-col justify-center items-center text-gray-500">
              <FaUsersSlash size={124} />
              <p className="mt-2 text-lg">No users found</p>
            </div>
          </td>
        </tr>
      );
    }

    return users.map((user: UsersType | undefined, index: number) => {
      if (!user) return null;

      return (
        <tr key={user.userId}>
          <td>{startNumber + index + 1}</td>
          <td>{maskText(user.username, 4)}</td>
          <td>{maskText(user.email, 5)}</td>
          <td>
            {user.created_at ? formatDate(user.created_at) + ' WIB' : null}
          </td>
          <td>
            {user.isAdmin ? (
              <Indicator status="Allowed Admin" />
            ) : (
              <Indicator status="Prohibited Admin" />
            )}
            {user.isPublisher ? (
              <Indicator status="Allowed Publisher" />
            ) : (
              <Indicator status="Prohibited Publisher" />
            )}
          </td>
          <td>
            <Box width="160px">
              {superAdminUsers.includes(user.username) ? (
                <span className="inline-flex items-center px-3 py-1 text-xs font-semibold text-white bg-blue-600 rounded-full">
                  <FaUserLock className="mr-1" />
                  Super Admin
                </span>
              ) : (
                <Button
                  iconLeft={<FaUserLock />}
                  variant="outline"
                  type="button"
                  onClick={() => handleControlPolicy(user)}
                >
                  Control Policy
                </Button>
              )}
            </Box>
          </td>
        </tr>
      );
    });
  };

  console.log('users', users);

  return (
    <Table hoverable responsive className="!overflow-x-auto">
      <thead className="whitespace-nowrap">
        <tr>
          {tableHeader.map((item, index) => (
            <th key={index}>
              {item === 'Created at' ? (
                <span
                  onClick={onToggleSort}
                  style={{
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {item}
                  {sortOrder === 'desc' ? <FaSortDown /> : <FaSortUp />}
                </span>
              ) : (
                item
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          // Skeleton rows
          <>
            {[...Array(5)].map((_, rowIdx) => (
              <tr key={rowIdx}>
                {Array(tableHeader.length)
                  .fill(0)
                  .map((_, colIdx) => (
                    <td key={colIdx}>
                      <Skeleton height={24} />
                    </td>
                  ))}
              </tr>
            ))}
          </>
        ) : (
          renderUsers()
        )}
      </tbody>
    </Table>
  );
};

export default UsersTable;
